import * as THREE from 'three';
import type { AudioManager } from '../audio/AudioManager';
import type { PlayerController } from '../player/PlayerController';
import type { Viewmodel } from '../player/Viewmodel';
import type { WeaponId } from '../weapons/WeaponDefs';
import { buildProcPickup } from '../player/ProcWeapons';
import { buildItemPickup, type ItemPickupKind } from './ProcPickups';

export type PickupKind =
  | 'pistol_clip'
  | 'shells'
  | 'mp5_clip'
  | 'ak_clip'
  | 'weapon_shotgun'
  | 'weapon_sawedoff'
  | 'weapon_mp5'
  | 'weapon_ak47'
  | 'weapon_gl'
  | 'weapon_rpg'
  | 'grenades'
  | 'rockets'
  | 'medi_small'
  | 'medi_large'
  | 'armor'
  | 'superhealth';

export interface PickupSpawnDef {
  kind: PickupKind;
  x: number;
  y: number;
  z: number;
}

const WEAPON_PICKUP_MODEL: Partial<Record<PickupKind, WeaponId>> = {
  weapon_shotgun: 'shotgun',
  weapon_sawedoff: 'sawedoff',
  weapon_mp5: 'mp5',
  weapon_ak47: 'ak47',
  weapon_gl: 'gl',
  weapon_rpg: 'rpg',
};

const PICKUP_SCALE: Record<PickupKind, number> = {
  pistol_clip: 0.55,
  shells: 0.5,
  mp5_clip: 0.55,
  ak_clip: 0.55,
  weapon_shotgun: 0.7,
  weapon_sawedoff: 0.7,
  weapon_mp5: 0.65,
  weapon_ak47: 0.7,
  weapon_gl: 0.7,
  weapon_rpg: 0.75,
  grenades: 0.5,
  rockets: 0.5,
  medi_small: 0.45,
  medi_large: 0.55,
  armor: 1.0,
  superhealth: 0.55,
};

/** Armor = teal; ammo = warm brass; weapons = green; meds = red; mega = gold. */
const GLOW_ARMOR = 0x44ccff;
const GLOW_AMMO = 0xffb000;
const GLOW_WEAPON = 0x66ff99;
const GLOW_MED = 0xff4466;
const GLOW_SUPER = 0xffee88;

const PICKUP_GLOW: Record<PickupKind, number> = {
  pistol_clip: GLOW_AMMO,
  shells: GLOW_AMMO,
  mp5_clip: GLOW_AMMO,
  ak_clip: GLOW_AMMO,
  weapon_shotgun: GLOW_WEAPON,
  weapon_sawedoff: GLOW_WEAPON,
  weapon_mp5: GLOW_WEAPON,
  weapon_ak47: GLOW_WEAPON,
  weapon_gl: GLOW_WEAPON,
  weapon_rpg: GLOW_WEAPON,
  grenades: GLOW_AMMO,
  rockets: GLOW_AMMO,
  medi_small: GLOW_MED,
  medi_large: GLOW_MED,
  armor: GLOW_ARMOR,
  superhealth: GLOW_SUPER,
};

interface LivePickup {
  kind: PickupKind;
  root: THREE.Group;
  /** Visual mesh only (no aura sprite) — used for look-at bounds. */
  model: THREE.Object3D;
  baseY: number;
  phase: number;
  alive: boolean;
  emergency?: boolean;
}

const PICKUP_LABELS: Record<PickupKind, string> = {
  pistol_clip: 'Pistol ammo +12',
  shells: 'Shotgun shells +8',
  mp5_clip: 'MP5 ammo +30',
  ak_clip: 'AK ammo +30',
  weapon_shotgun: 'Picked up Shotgun',
  weapon_sawedoff: 'Picked up Double-Barrel Shotgun',
  weapon_mp5: 'Picked up MP5',
  weapon_ak47: 'Picked up AK-47',
  weapon_gl: 'Picked up Grenade Launcher',
  weapon_rpg: 'Picked up Rocket Launcher',
  grenades: 'Grenades +6',
  rockets: 'Rockets +2',
  medi_small: 'Medkit +25 HP',
  medi_large: 'Medkit +50 HP',
  armor: 'Armor +50',
  superhealth: 'Super Health +100',
};

const LOOK_LABELS: Record<PickupKind, string> = {
  pistol_clip: 'Pistol ammo',
  shells: 'Shotgun shells',
  mp5_clip: 'MP5 ammo',
  ak_clip: 'AK ammo',
  weapon_shotgun: 'Shotgun',
  weapon_sawedoff: 'Double-Barrel',
  weapon_mp5: 'MP5',
  weapon_ak47: 'AK-47',
  weapon_gl: 'Grenade Launcher',
  weapon_rpg: 'Rocket Launcher',
  grenades: 'Grenades',
  rockets: 'Rockets',
  medi_small: 'Medkit +25',
  medi_large: 'Medkit +50',
  armor: 'Armor +50',
  superhealth: 'Super Health',
};

export interface PickupLookTip {
  label: string;
  x: number;
  y: number;
}

const LOOK_MAX_DIST = 9.5;
/** Smallest look-at box so thin mags are still hittable without floating above the mesh. */
const LOOK_MIN_SIZE = 0.16;

const WEAPON_GRANT: Partial<Record<PickupKind, WeaponId>> = {
  weapon_shotgun: 'shotgun',
  weapon_sawedoff: 'sawedoff',
  weapon_mp5: 'mp5',
  weapon_ak47: 'ak47',
  weapon_gl: 'gl',
  weapon_rpg: 'rpg',
};

/** Arena pickups — bobbing models, proximity collect. */
export class PickupSystem {
  private readonly group = new THREE.Group();
  private readonly pickups: LivePickup[] = [];
  private readonly templates = new Map<PickupKind, THREE.Group>();
  private readonly tmp = new THREE.Vector3();
  private readonly lookDir = new THREE.Vector3();
  private readonly lookNdc = new THREE.Vector3();
  private readonly lookCenter = new THREE.Vector3();
  private readonly lookSize = new THREE.Vector3();
  private readonly lookHit = new THREE.Vector3();
  private readonly lookRay = new THREE.Raycaster();
  private readonly lookBox = new THREE.Box3();
  private ready = false;
  private toastQueue: string[] = [];

  constructor(
    private readonly scene: THREE.Scene,
    private readonly audio: AudioManager,
  ) {
    this.group.name = 'Pickups';
    this.scene.add(this.group);
  }

  async load(spawns: PickupSpawnDef[]): Promise<void> {
    const kinds = [...new Set(spawns.map((s) => s.kind))];
    for (const kind of kinds) this.ensureTemplate(kind);
    for (const spawn of spawns) this.place(spawn.kind, spawn.x, spawn.y, spawn.z);
    this.ready = true;
  }

  collectCullRoots(out: THREE.Object3D[]): void {
    for (const p of this.pickups) {
      if (p.alive) out.push(p.root);
    }
  }

  /** Live count of emergency medkits still on the floor. */
  emergencyCount(): number {
    let n = 0;
    for (const p of this.pickups) {
      if (p.alive && p.emergency) n += 1;
    }
    return n;
  }

  /** Drop a pickup at a world point (e.g. enemy death). */
  spawnAt(kind: PickupKind, x: number, y: number, z: number, emergency = false): boolean {
    this.ensureTemplate(kind);
    return this.place(kind, x, y, z, emergency);
  }

  private ensureTemplate(kind: PickupKind): void {
    if (this.templates.has(kind)) return;
    const weaponId = WEAPON_PICKUP_MODEL[kind];
    const root = weaponId ? buildProcPickup(weaponId) : buildItemPickup(kind as ItemPickupKind);
    root.traverse((o) => {
      const m = o as THREE.Mesh;
      if (!m.isMesh) return;
      m.castShadow = false;
      m.receiveShadow = false;
      m.frustumCulled = false;
    });
    applyPickupGlow(root, PICKUP_GLOW[kind]);
    const box = new THREE.Box3().setFromObject(root);
    const size = box.getSize(new THREE.Vector3());
    const maxDim = Math.max(size.x, size.y, size.z) || 1;
    root.scale.setScalar(PICKUP_SCALE[kind] / maxDim);
    box.setFromObject(root);
    const center = box.getCenter(new THREE.Vector3());
    root.position.sub(center);
    root.position.y -= box.min.y;
    if (kind.startsWith('weapon_')) {
      root.rotation.x = -Math.PI * 0.12;
      root.rotation.z = Math.PI * 0.08;
    }
    this.templates.set(kind, root);
  }

  private place(kind: PickupKind, x: number, y: number, z: number, emergency = false): boolean {
    const tmpl = this.templates.get(kind);
    if (!tmpl) return false;
    const root = new THREE.Group();
    const model = tmpl.clone(true);
    root.add(model);
    root.add(makeAuraSprite(PICKUP_GLOW[kind], PICKUP_SCALE[kind] * 1.25));
    root.position.set(x, y, z);
    root.userData.ignoreHit = true;
    root.userData.pickupKind = kind;
    root.traverse((o) => {
      o.userData.ignoreHit = true;
      o.raycast = () => {};
    });
    this.group.add(root);
    this.pickups.push({
      kind,
      root,
      model,
      baseY: y,
      phase: Math.random() * Math.PI * 2,
      alive: true,
      emergency,
    });
    this.ready = true;
    return true;
  }

  update(player: PlayerController, viewmodel: Viewmodel, dt: number): void {
    if (!this.ready) return;
    const pos = player.body.position;
    for (const p of this.pickups) {
      if (!p.alive) continue;
      p.phase += dt * 2.2;
      p.root.position.y = p.baseY + Math.sin(p.phase) * 0.08;
      p.root.rotation.y += dt * 1.4;

      p.root.getWorldPosition(this.tmp);
      const dx = this.tmp.x - pos.x;
      const dz = this.tmp.z - pos.z;
      const dy = this.tmp.y - (pos.y + 0.9);
      if (dx * dx + dz * dz + dy * dy * 0.35 > 1.15 * 1.15) continue;

      if (this.apply(p.kind, player, viewmodel)) {
        p.alive = false;
        this.group.remove(p.root);
        p.root.visible = false;
        this.toastQueue.push(PICKUP_LABELS[p.kind]);
      }
    }
  }

  /** Pop next pickup announcement for HUD toast, if any. */
  consumeToast(): string | null {
    return this.toastQueue.shift() ?? null;
  }

  /**
   * Tooltip for the pickup the crosshair ray actually hits (visual AABB),
   * not a floating point above the mesh.
   */
  lookTip(
    camera: THREE.Camera,
    scene: THREE.Object3D,
    width: number,
    height: number,
  ): PickupLookTip | null {
    if (!this.ready || width < 8 || height < 8) return null;

    camera.getWorldPosition(this.tmp);
    camera.getWorldDirection(this.lookDir);
    this.lookRay.set(this.tmp, this.lookDir);
    this.lookRay.near = 0.05;
    this.lookRay.far = LOOK_MAX_DIST;

    let best: LivePickup | null = null;
    let bestDist = Infinity;

    for (const p of this.pickups) {
      if (!p.alive) continue;
      this.visualLookBox(p, this.lookBox);
      if (!this.lookRay.ray.intersectBox(this.lookBox, this.lookHit)) continue;
      const dist = this.lookHit.distanceTo(this.tmp);
      if (dist < 0.2 || dist > LOOK_MAX_DIST || dist >= bestDist) continue;
      bestDist = dist;
      best = p;
    }

    if (!best) return null;
    if (this.blockedByWorld(scene, bestDist)) return null;

    this.visualLookBox(best, this.lookBox);
    this.lookBox.getCenter(this.lookCenter);
    this.lookNdc.set(this.lookCenter.x, this.lookBox.max.y, this.lookCenter.z);
    this.lookNdc.project(camera);
    if (this.lookNdc.z < -1 || this.lookNdc.z > 1) return null;

    return {
      label: LOOK_LABELS[best.kind],
      x: Math.min(width - 40, Math.max(40, (this.lookNdc.x * 0.5 + 0.5) * width)),
      y: Math.min(height - 24, Math.max(22, (-this.lookNdc.y * 0.5 + 0.5) * height)),
    };
  }

  /** World AABB of the rendered item, padded slightly, aura sprite excluded. */
  private visualLookBox(p: LivePickup, out: THREE.Box3): THREE.Box3 {
    out.setFromObject(p.model);
    out.expandByScalar(0.03);
    out.getCenter(this.lookCenter);
    out.getSize(this.lookSize);
    const hx = Math.max(this.lookSize.x, LOOK_MIN_SIZE) * 0.5;
    const hy = Math.max(this.lookSize.y, LOOK_MIN_SIZE) * 0.5;
    const hz = Math.max(this.lookSize.z, LOOK_MIN_SIZE) * 0.5;
    out.min.set(this.lookCenter.x - hx, this.lookCenter.y - hy, this.lookCenter.z - hz);
    out.max.set(this.lookCenter.x + hx, this.lookCenter.y + hy, this.lookCenter.z + hz);
    return out;
  }

  private blockedByWorld(scene: THREE.Object3D, dist: number): boolean {
    this.lookRay.far = Math.max(0.08, dist - 0.04);
    const hits = this.lookRay.intersectObject(scene, true);
    for (const h of hits) {
      if (isIgnoredHitTarget(h.object)) continue;
      return true;
    }
    return false;
  }

  private apply(kind: PickupKind, player: PlayerController, viewmodel: Viewmodel): boolean {
    const grant = WEAPON_GRANT[kind];
    if (grant) {
      viewmodel.grantWeapon(grant);
      this.audio.play('pickupWeapon', { volume: 0.85 });
      return true;
    }

    switch (kind) {
      case 'pistol_clip':
        viewmodel.addPistolReserve(12);
        this.audio.play('pickupAmmo', { volume: 0.8 });
        return true;
      case 'shells':
        viewmodel.addShotgunShells(8);
        this.audio.play('pickupAmmo', { volume: 0.85 });
        return true;
      case 'mp5_clip':
        viewmodel.addReserve('mp5', 30);
        this.audio.play('pickupAmmo', { volume: 0.8 });
        return true;
      case 'ak_clip':
        viewmodel.addReserve('ak47', 30);
        this.audio.play('pickupAmmo', { volume: 0.8 });
        return true;
      case 'grenades':
        viewmodel.addReserve('gl', 6);
        this.audio.play('pickupAmmo', { volume: 0.8 });
        return true;
      case 'rockets':
        viewmodel.addReserve('rpg', 2);
        this.audio.play('pickupAmmo', { volume: 0.8 });
        return true;
      case 'medi_small':
        if (!player.heal(25)) return false;
        this.audio.play('pickupItem', { volume: 0.7 });
        return true;
      case 'medi_large':
        if (!player.heal(50)) return false;
        this.audio.play('pickupItem', { volume: 0.75 });
        return true;
      case 'armor':
        if (!player.addArmor(50)) return false;
        this.audio.play('pickupArmor', { volume: 0.8 });
        return true;
      case 'superhealth':
        if (!player.heal(100)) return false;
        this.audio.play('pickupSuper', { volume: 0.85 });
        return true;
      default:
        return false;
    }
  }
}

/**
 * Exterior edge glow only (EdgesGeometry) — no inflated shell, so cavities /
 * inner faces don't light up.
 */
function applyPickupGlow(root: THREE.Object3D, color: number): void {
  const meshes: THREE.Mesh[] = [];
  root.traverse((o) => {
    const m = o as THREE.Mesh;
    if (m.isMesh && m.geometry && !m.userData.skipGlow) meshes.push(m);
  });

  for (const m of meshes) {
    m.geometry.computeBoundingSphere();
    const r = m.geometry.boundingSphere?.radius ?? 0;
    if (r < 0.004) continue;

    const edges = new THREE.EdgesGeometry(m.geometry, 40);
    const outline = new THREE.LineSegments(
      edges,
      new THREE.LineBasicMaterial({
        color,
        transparent: true,
        opacity: 0.55,
        depthTest: true,
        depthWrite: false,
        toneMapped: false,
      }),
    );
    outline.frustumCulled = false;
    outline.userData.ignoreHit = true;
    outline.raycast = () => {};
    m.add(outline);
  }
}

let auraMap: THREE.CanvasTexture | null = null;

function auraTexture(): THREE.CanvasTexture {
  if (auraMap) return auraMap;
  const s = 64;
  const c = document.createElement('canvas');
  c.width = s;
  c.height = s;
  const ctx = c.getContext('2d')!;
  const g = ctx.createRadialGradient(s * 0.5, s * 0.5, 0, s * 0.5, s * 0.5, s * 0.5);
  g.addColorStop(0, 'rgba(255,255,255,0.95)');
  g.addColorStop(0.35, 'rgba(255,255,255,0.35)');
  g.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, s, s);
  auraMap = new THREE.CanvasTexture(c);
  auraMap.colorSpace = THREE.NoColorSpace;
  return auraMap;
}

function makeAuraSprite(color: number, size: number): THREE.Sprite {
  const mat = new THREE.SpriteMaterial({
    map: auraTexture(),
    color,
    transparent: true,
    opacity: 0.72,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    toneMapped: false,
  });
  const sprite = new THREE.Sprite(mat);
  sprite.scale.set(size, size * 0.55, 1);
  sprite.position.y = 0.04;
  sprite.frustumCulled = false;
  sprite.userData.ignoreHit = true;
  sprite.userData.skipGlow = true;
  sprite.raycast = () => {};
  return sprite;
}

function isIgnoredHitTarget(obj: THREE.Object3D): boolean {
  let o: THREE.Object3D | null = obj;
  while (o) {
    if (o.userData?.ignoreHit) return true;
    o = o.parent;
  }
  return false;
}

/** Match TestArena horizontal scale (H = 1.7). */
const H = 1.7;
const p = (x: number, y: number, z: number) => ({ x: x * H, y, z: z * H });

/** Default pickup layout for the military compound arena. */
export const ARENA_PICKUPS: PickupSpawnDef[] = [
  { kind: 'pistol_clip', ...p(-7.5, 0.15, 3.2) },
  { kind: 'pistol_clip', ...p(8.5, 0.15, -5.5) },
  { kind: 'shells', ...p(0.8, 0.15, -0.8) },
  { kind: 'shells', ...p(-14, 1.85, -2.2) },
  { kind: 'shells', ...p(12, 0.15, -7.5) },
  { kind: 'medi_small', ...p(-2.2, 0.15, 11.2) },
  { kind: 'medi_large', ...p(15.5, 0.25, 4.0) },
  { kind: 'armor', ...p(-13.5, 1.85, 3.2) },
  { kind: 'armor', ...p(16.5, 0.25, 8.8) },
  { kind: 'superhealth', ...p(2, 3.45, -10) },
  { kind: 'pistol_clip', ...p(-11.2, 1.85, -3.0) },
  { kind: 'medi_small', ...p(9.5, 0.15, 7.5) },
];
